import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { TriangleDown as TriangleDownIcon } from "@air/next-icons";
import { tailwindMerge, tailwindVariants } from "@air/tailwind-variants";
import classNames from "classnames";
import { useCombobox, useMultipleSelection } from "downshift";
import constant from "lodash/constant";
import isEqual from "lodash/isEqual";
import noop from "lodash/noop";
import { matchSorter } from "match-sorter";
import maxSize from "popper-max-size-modifier";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePopper } from "react-popper";
import { useDebouncedCallback } from "use-debounce";
import { InputClearButton } from "~/components/InputClearButton";
import { SelectChipOptionContainer } from "~/components/Select/components/SelectChipOptionContainer";
import { applyMaxSize, sameWidth } from "~/components/Select/popperModifiers";
import { SELECT_OPTION, SELECTED_CHIP } from "~/components/Select/shared/testIDs";
import { CLEAR_SELECTED_VALUES_BUTTON, SELECT_INPUT_FIELD } from "~/constants/testIDs";
import { SelectChip } from "./components/SelectChip";
import { SelectCreateOption } from "./components/SelectCreateOption";
import { SelectEmptyOption } from "./components/SelectEmptyOption";
import { SelectLoadingOption } from "./components/SelectLoadingOption";
function alphabeticallySortedFilterFunc(items, inputValue, selectedOptions) {
    const matchedOptions = matchSorter(items, inputValue, {
        keys: [
            "label"
        ]
    });
    const selectedValues = selectedOptions.map((option)=>option.value);
    return matchedOptions.filter((option)=>!selectedValues.includes(option.value));
}
// use to avoid alphabatising the initial list/results
export function orderedSortedFilterFunc(items, inputValue, selectedOptions) {
    const matchedOptions = matchSorter(items, inputValue, {
        baseSort: (a, b)=>a.index < b.index ? -1 : 1,
        keys: [
            "label"
        ]
    });
    const selectedValues = selectedOptions.map((option)=>option.value);
    return matchedOptions.filter((option)=>!selectedValues.includes(option.value));
}
export const select = tailwindVariants({
    base: "relative flex rounded [&>[data-popper-escaped='true']]:hidden [&>[data-popper-reference-hidden='true']]:hidden",
    variants: {
        size: {
            medium: "min-h-[32px]",
            large: "min-h-[40px]",
            "extra-large": "min-h-[48px]"
        }
    },
    defaultVariants: {
        size: "large"
    }
});
export const inputSelect = tailwindVariants({
    base: "m-0.5 rounded border-none bg-transparent p-0 font-medium text-grey-11 outline-none ring-0 focus:border-none focus:ring-0",
    variants: {
        size: {
            medium: "text-12",
            large: "text-12",
            "extra-large": "text-14"
        }
    },
    defaultVariants: {
        size: "large"
    }
});
export const placeholderSelect = tailwindVariants({
    base: "m-0.5 px-0.5 text-grey-7",
    variants: {
        size: {
            medium: "text-16 md:text-12",
            large: "text-16 md:text-12",
            "extra-large": "text-16 md:text-14"
        }
    },
    defaultVariants: {
        size: "large"
    }
});
const _Select = (param)=>{
    let { alwaysShowClearItemButton, autoFocus, beforeCreate = constant(undefined), chipRenderer, className, closeMenuOnSelect, createVerb, "data-testid": testId, disabled, filterFunc = alphabeticallySortedFilterFunc, hasDropdownOverlay, hasError, hideClearWhenNotFocused, highlightFirstOptionOnOpen = false, highlightFirstOptionOnSearch = false, inputContainerClassName, isLoading, isSearchable, isSingleSelect, isSticky = false, isClearable, listItemRenderer, maxDropdownHeight, noOptionsText, onCreate, onInputChange, onOptionsOpen, onOptionsClose, onSelectionChange, options, placeholder = "", pressEnterToAdd = true, preventPressEnterToAddWhenSearching = false, renderAsInput, selectedOptions = [], showTriangle = false, size = "large", createOptionFilter = constant(true) } = param;
    const [isCreating, setIsCreating] = useState(false);
    const [selectedItems, setSelectedItemsState] = useState(selectedOptions);
    const [inputItems, setInputItems] = useState(filterFunc(options, "", selectedOptions));
    const comboBoxRef = useRef(null);
    const popoverRef = useRef(null);
    const inputRef = useRef(null);
    const labelRef = useRef(null);
    const inputValueRef = useRef("");
    const selectedItemsRef = useRef(selectedOptions);
    const optionsRef = useRef(options);
    const onOptionsOpenRef = useRef(onOptionsOpen);
    onOptionsOpenRef.current = onOptionsOpen;
    const onOptionsCloseRef = useRef(onOptionsClose);
    onOptionsCloseRef.current = onOptionsClose;
    const { styles, attributes, forceUpdate } = usePopper(comboBoxRef.current, popoverRef.current, {
        placement: "bottom-start",
        strategy: "fixed",
        modifiers: [
            maxSize,
            applyMaxSize,
            sameWidth,
            {
                name: "eventListeners",
                options: {
                    scroll: isSticky,
                    resize: false
                }
            },
            {
                name: "offset",
                options: {
                    offset: [
                        0,
                        8
                    ]
                }
            }
        ]
    });
    const [debouncedUpdatePopper] = useDebouncedCallback(()=>{
        if (forceUpdate) forceUpdate();
    }, 200);
    const [selectedOptionValues, selectedOptionLabels] = useMemo(()=>{
        return [
            selectedItems.map((option)=>option.value),
            selectedItems.map((option)=>option.label)
        ];
    }, [
        selectedItems
    ]);
    const focusSearchInput = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        if (inputRef.current && isSearchable) {
            inputRef.current.focus();
        }
    };
    // This is called whenever downshift changes its internal selection state. We pass those changes
    // up to the parent component.
    const onSelectedItemsChange = useCallback((param)=>{
        let { selectedItems: chips } = param;
        if (!!inputRef.current && isSearchable && !closeMenuOnSelect) {
            inputRef.current.focus();
        }
        if (chips) {
            setSelectedItemsState(chips);
            const filteredItems = filterFunc(optionsRef.current, inputValueRef.current, chips);
            setInputItems(filteredItems);
            const previouslySelected = selectedItemsRef.current;
            selectedItemsRef.current = chips;
            let addedChips = [];
            let removedChips = [];
            if (isSingleSelect && chips.length === previouslySelected.length) {
                addedChips = chips;
                removedChips = previouslySelected;
            } else if (chips.length > previouslySelected.length) {
                const previousChipValues = previouslySelected.map((chip)=>chip.value);
                addedChips = chips.filter((chip)=>!previousChipValues.includes(chip.value));
            } else if (previouslySelected.length > chips.length) {
                const chipValues = chips.map((chip)=>chip.value);
                removedChips = previouslySelected.filter((chip)=>!chipValues.includes(chip.value));
            }
            onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange({
                chips,
                addedChips,
                removedChips
            });
        }
    }, [
        closeMenuOnSelect,
        filterFunc,
        isSearchable,
        isSingleSelect,
        onSelectionChange
    ]);
    const { getDropdownProps, addSelectedItem, removeSelectedItem, setSelectedItems, reset } = useMultipleSelection({
        selectedItems,
        onSelectedItemsChange,
        itemToString: (item)=>item.value
    });
    const onClear = useCallback((e)=>{
        if (!isClearable) return;
        e.preventDefault();
        e.stopPropagation();
        reset();
    }, [
        isClearable,
        reset
    ]);
    const { isOpen, getMenuProps, getInputProps, getComboboxProps, getItemProps, openMenu, getLabelProps, selectItem, setInputValue, inputValue, highlightedIndex, closeMenu, setHighlightedIndex } = useCombobox({
        initialIsOpen: autoFocus,
        selectedItem: null,
        items: inputItems,
        onInputValueChange: (param)=>{
            let { inputValue: _inputValue } = param;
            const normalizedInputVal = _inputValue || "";
            if (!!onInputChange && inputValueRef.current !== normalizedInputVal) {
                onInputChange(normalizedInputVal);
            }
            const filteredItems = filterFunc(options, normalizedInputVal, selectedItemsRef.current);
            if (labelRef.current) {
                labelRef.current.dataset.value = normalizedInputVal;
            }
            debouncedUpdatePopper();
            setInputItems(filteredItems);
            inputValueRef.current = normalizedInputVal;
        },
        stateReducer: (_, actionAndChanges)=>{
            const { changes, type } = actionAndChanges;
            switch(type){
                //TODO: 'changes' will include isOpen: false here, causing the menu to close if the input is blurred.
                // This is desired if you're clicking outside the element, but if you tab to the onClear button it will:
                // 1) close the menu
                // 2) reopen the menu
                // 3) return focus to the input.
                // Effectively preventing tabing to the clear button. However, if you return isOpen: true here the menu wont
                // close when you click outside the component. We need to find a way to allow tabbing within the input while
                // preserving the click outside functionality.
                case useCombobox.stateChangeTypes.InputBlur:
                    return {
                        ...changes,
                        inputValue: ""
                    };
                case useCombobox.stateChangeTypes.FunctionOpenMenu:
                    {
                        return {
                            ...changes,
                            isOpen: disabled ? false : changes.isOpen,
                            ...highlightFirstOptionOnOpen === true && {
                                highlightedIndex: Array.isArray(inputItems) && inputItems.length > 0 ? 0 : -1
                            }
                        };
                    }
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        isOpen: true,
                        inputValue: ""
                    };
                default:
                    return changes;
            }
        },
        onStateChange: (param)=>{
            let { type, selectedItem } = param;
            switch(type){
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        if (isSingleSelect) {
                            setSelectedItems([
                                selectedItem
                            ]);
                        } else if (selectedOptionValues.includes(selectedItem.value)) {
                            removeSelectedItem(selectedItem);
                        } else {
                            addSelectedItem(selectedItem);
                        }
                        selectItem(null);
                        if (closeMenuOnSelect) {
                            closeMenu();
                        }
                    }
                    break;
                default:
                    break;
            }
        }
    });
    // Highlight the first option that matches the user's search.
    useEffect(()=>{
        if (highlightFirstOptionOnSearch === false) return;
        setHighlightedIndex(inputItems.findIndex((item)=>item.label.toLowerCase() === inputValue.toLowerCase()));
    }, [
        highlightFirstOptionOnSearch,
        inputItems,
        inputValue,
        setHighlightedIndex
    ]);
    // Focus the input when the list is opened, and re-evalute the popper position.
    useEffect(()=>{
        if (isOpen) {
            var _onOptionsOpenRef_current;
            (_onOptionsOpenRef_current = onOptionsOpenRef.current) === null || _onOptionsOpenRef_current === void 0 ? void 0 : _onOptionsOpenRef_current.call(onOptionsOpenRef);
            if (forceUpdate) forceUpdate();
            if (!!inputRef.current && isSearchable) {
                inputRef.current.focus();
            }
        } else {
            var _onOptionsCloseRef_current;
            (_onOptionsCloseRef_current = onOptionsCloseRef.current) === null || _onOptionsCloseRef_current === void 0 ? void 0 : _onOptionsCloseRef_current.call(onOptionsCloseRef);
        }
    }, [
        isOpen,
        forceUpdate,
        isSearchable
    ]);
    // If the input value changes, and it would necessitate the create button showing, show the create button.
    // Else, hide it.
    useEffect(()=>{
        if (!onCreate) return;
        if (inputValue.length > 0) {
            const hasChipAsOption = inputItems.some((item)=>item.label.toLowerCase() === inputValue.toLowerCase());
            const hasChipSelected = selectedOptionLabels.includes(inputValue);
            const isInputValid = createOptionFilter(inputValue);
            if (isInputValid) {
                if (hasChipAsOption || hasChipSelected) {
                    setIsCreating(false);
                } else {
                    setIsCreating(true);
                }
            } else {
                setIsCreating(false);
            }
        } else {
            setIsCreating(false);
        }
    }, [
        createOptionFilter,
        inputItems,
        inputValue,
        onCreate,
        selectedOptionLabels
    ]);
    // If new options are passed in, update the multi-selects internal state if theyre different.
    useEffect(()=>{
        if (!isEqual(options, optionsRef.current)) {
            optionsRef.current = options;
            const filteredItems = filterFunc(options, inputValueRef.current, selectedItemsRef.current);
            setInputItems(filteredItems);
        }
    }, [
        filterFunc,
        options
    ]);
    // If the selectedOptions prop changes, make sure its actually changed before updating
    // this internal state.
    useEffect(()=>{
        if (!isEqual(selectedOptions, selectedItemsRef.current)) {
            selectedItemsRef.current = selectedOptions;
            setSelectedItemsState(selectedOptions);
            const filteredItems = filterFunc(optionsRef.current, inputValueRef.current, selectedOptions);
            setInputItems(filteredItems);
        }
    }, [
        filterFunc,
        selectedOptions
    ]);
    // Adjust the popper positioning if the selected items changes
    // incase they've overflowed to a new row and the dropdown needs to reposition
    useEffect(()=>{
        if (selectedItems && forceUpdate) {
            forceUpdate();
        }
    }, [
        selectedItems,
        forceUpdate
    ]);
    // Add a new item when create is clicked
    const onClickCreate = useCallback(()=>{
        if (!inputValue || !onCreate) return;
        const newChip = {
            label: inputValue,
            value: inputValue
        };
        const { error } = beforeCreate(newChip) || {
            error: undefined
        };
        if (error) {
            return;
        }
        if (isSingleSelect) {
            setSelectedItems([
                newChip
            ]);
            setSelectedItemsState([
                newChip
            ]);
        } else {
            selectItem(newChip);
            setSelectedItemsState([
                ...selectedItems,
                newChip
            ]);
        }
        selectItem(null);
        selectedItemsRef.current = [
            ...selectedItems,
            newChip
        ];
        onCreate(inputValue);
        if (closeMenuOnSelect) {
            closeMenu();
            setTimeout(()=>{
                var _inputRef_current;
                return (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
            });
        }
        if (inputRef.current && isSearchable) inputRef.current.focus();
    }, [
        beforeCreate,
        closeMenu,
        closeMenuOnSelect,
        inputValue,
        isSearchable,
        isSingleSelect,
        onCreate,
        selectItem,
        selectedItems,
        setSelectedItems
    ]);
    // Memoize popper styles so we're no repainting the div they're passed to every re-render.
    const popperStyles = useMemo(()=>{
        const maxStylesHeight = styles.popper.maxHeight ? parseInt(styles.popper.maxHeight) : null;
        return {
            ...styles.popper,
            borderRadius: 4,
            maxHeight: maxDropdownHeight && maxStylesHeight ? Math.min(maxStylesHeight, maxDropdownHeight) : "unset",
            zIndex: 2,
            overflow: "auto",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.2), 0px 1px 3px rgba(0, 0, 0, 0.15), 0px 0px 2px rgba(0, 0, 0, 0.25)"
        };
    }, [
        maxDropdownHeight,
        styles.popper
    ]);
    const listItems = useMemo(()=>{
        return inputItems.map((item, index)=>{
            // We dont spread this because it attaches a "mousemove" prop that hinders performance,
            // and isnt used by the component.
            const itemProps = getItemProps({
                item,
                index
            });
            const isActive = highlightedIndex === index;
            return /*#__PURE__*/ _jsx("button", {
                className: classNames("w-full items-start text-start", {
                    isActive
                }),
                "data-testid": "".concat(SELECT_OPTION, "-").concat(item.label),
                onClick: itemProps.onClick,
                onMouseEnter: itemProps.onMouseMove,
                onMouseLeave: itemProps.onMouseMove,
                id: itemProps.id,
                ref: itemProps.ref,
                role: itemProps.role,
                type: "button",
                children: listItemRenderer ? listItemRenderer(item) : /*#__PURE__*/ _jsx(SelectChipOptionContainer, {
                    className: classNames({
                        "bg-grey-4 [&>*]:bg-grey-5": isActive
                    }),
                    children: /*#__PURE__*/ _jsx(SelectChip, {
                        chip: item,
                        size: size
                    })
                })
            }, "".concat(item.label).concat(index));
        });
    }, [
        inputItems,
        getItemProps,
        highlightedIndex,
        listItemRenderer,
        size
    ]);
    const inputProps = getInputProps({
        ref: inputRef,
        size: 1,
        tabIndex: 0
    }, {
        /**
       * Suppress the ref error that occurs when the inputRef is not defined.
       * @see https://github.com/downshift-js/downshift/issues/1531
       */ suppressRefError: true
    });
    // The chips to render next to the input.
    const selectedChips = useMemo(()=>{
        return selectedItems.map((chip, index)=>{
            if (chipRenderer) return /*#__PURE__*/ _createElement("div", {
                ...inputProps,
                key: "selected-chip-".concat(chip.value, "-").concat(index)
            }, chipRenderer(chip, removeSelectedItem, {
                isOpen
            }));
            return /*#__PURE__*/ _jsx(SelectChip, {
                "data-testid": SELECTED_CHIP,
                chip: chip,
                size: size,
                onDeselect: alwaysShowClearItemButton || isOpen ? removeSelectedItem : undefined
            }, "selected-chip-".concat(chip.value, "-").concat(index));
        });
    }, [
        selectedItems,
        chipRenderer,
        inputProps,
        removeSelectedItem,
        isOpen,
        size,
        alwaysShowClearItemButton
    ]);
    const showClearFieldButton = useMemo(()=>{
        if (!isClearable) return false;
        if (hideClearWhenNotFocused && !isOpen) return false;
        return isClearable && !!selectedChips.length && !disabled;
    }, [
        disabled,
        hideClearWhenNotFocused,
        isClearable,
        isOpen,
        selectedChips
    ]);
    const selectContent = useMemo(()=>{
        return /*#__PURE__*/ _jsxs("div", {
            className: tailwindMerge("relative flex w-full flex-wrap items-center justify-start rounded bg-transparent transition-colors", inputContainerClassName, disabled ? "pointer-events-none" : "", isOpen ? "ring-2 ring-blue-9" : hasError ? "ring-1 ring-red-9" : "ring-1 ring-grey-5 hover:ring-grey-7", isOpen && hasError ? "ring-2 ring-red-9" : "", isOpen && isSearchable ? "cursor-text" : "", renderAsInput && !isOpen ? "ring-1 ring-grey-5" : "", renderAsInput && !hasError && !isOpen ? "hover:ring-1 hover:ring-grey-7" : "", !renderAsInput && !isOpen && "hover:bg-grey-2", size === "medium" ? "px-1.5 py-0" : "p-1.5"),
            ...getComboboxProps(getDropdownProps({
                onClick: isOpen ? focusSearchInput : openMenu,
                onFocus: isOpen ? noop : openMenu,
                ref: comboBoxRef,
                tabIndex: -1
            })),
            children: [
                selectedChips.length || isOpen ? selectedChips : /*#__PURE__*/ _jsx("p", {
                    className: placeholderSelect({
                        size,
                        className: "placeholder"
                    }),
                    children: placeholder
                }),
                /*#__PURE__*/ _jsx("label", {
                    className: classNames("auto-width", {
                        isHidden: isSingleSelect ? !isSearchable && !!selectedChips.length : !isSearchable,
                        ["cursor-text"]: isOpen && isSearchable
                    }),
                    ...getLabelProps({
                        ref: labelRef
                    }),
                    children: isOpen && isSearchable && /*#__PURE__*/ _jsx("input", {
                        className: inputSelect({
                            size,
                            className: showClearFieldButton ? "pr-6" : ""
                        }),
                        "data-testid": SELECT_INPUT_FIELD,
                        ...inputProps,
                        onKeyDown: (e)=>{
                            var _inputProps_onKeyDown;
                            (_inputProps_onKeyDown = inputProps.onKeyDown) === null || _inputProps_onKeyDown === void 0 ? void 0 : _inputProps_onKeyDown.call(inputProps, e);
                            if (pressEnterToAdd && e.key === "Enter") {
                                const isListItemSelected = highlightedIndex !== -1;
                                // Take no action: if the user typed into the search field and we're performing a search
                                const isSearching = !!isLoading && inputValue.length !== 0 && !isListItemSelected;
                                if (preventPressEnterToAddWhenSearching === true && isSearching) return;
                                // Take no action: if there's a list item already selected
                                if (isListItemSelected) return;
                                if (isCreating && onCreate) {
                                    onClickCreate();
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                                if (!isCreating) {
                                    const chip = inputItems.find((item)=>item.label.toLowerCase() === inputValue.toLowerCase());
                                    if (!chip) return;
                                    setSelectedItems([
                                        ...selectedItems,
                                        chip
                                    ]);
                                    setInputValue("");
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }
                        }
                    })
                })
            ]
        });
    }, [
        disabled,
        getComboboxProps,
        getDropdownProps,
        getLabelProps,
        hasError,
        highlightedIndex,
        inputContainerClassName,
        inputItems,
        inputProps,
        inputValue,
        isCreating,
        isOpen,
        isLoading,
        isSearchable,
        isSingleSelect,
        onClickCreate,
        onCreate,
        openMenu,
        placeholder,
        pressEnterToAdd,
        preventPressEnterToAddWhenSearching,
        renderAsInput,
        selectedChips,
        selectedItems,
        setInputValue,
        setSelectedItems,
        showClearFieldButton,
        size
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": testId,
        className: select({
            size,
            className
        }),
        children: [
            isOpen && hasDropdownOverlay && /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 bg-transparent"
            }),
            isOpen ? /*#__PURE__*/ _jsx("div", {
                className: "flex flex-1",
                children: selectContent
            }) : /*#__PURE__*/ _jsx("button", {
                className: "flex flex-1 font-normal",
                disabled: disabled,
                "data-testid": "".concat(testId, "_TRIGGER"),
                type: "button",
                children: selectContent
            }),
            /*#__PURE__*/ _jsx("div", {
                style: popperStyles,
                ...attributes.popper,
                ...getMenuProps({
                    ref: popoverRef
                }),
                children: isOpen && /*#__PURE__*/ _jsxs("ul", {
                    className: "w-full list-none overflow-auto rounded bg-grey-1 p-3",
                    children: [
                        isLoading ? /*#__PURE__*/ _jsx(SelectLoadingOption, {}) : listItems,
                        (!isCreating || !inputValue) && !isLoading && !inputItems.length && /*#__PURE__*/ _jsx(SelectEmptyOption, {
                            noOptionsText: noOptionsText
                        }),
                        isCreating && !!inputValue && !!onCreate && /*#__PURE__*/ _jsx(SelectCreateOption, {
                            onClick: onClickCreate,
                            value: inputValue,
                            hideSpacer: !inputItems.length && !isLoading,
                            createVerb: createVerb
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "absolute inset-y-0 right-2.5 z-1 flex items-center",
                children: [
                    showClearFieldButton && /*#__PURE__*/ _jsx(InputClearButton, {
                        "data-testid": CLEAR_SELECTED_VALUES_BUTTON,
                        isVisible: true,
                        isFlex: true,
                        onClick: onClear
                    }),
                    showTriangle && /*#__PURE__*/ _jsx(TriangleDownIcon, {
                        className: "size-4 text-grey-10"
                    })
                ]
            })
        ]
    });
};
export const Select = /*#__PURE__*/ memo(_Select);
